export const PHONE_RE = /^\+7\d{10}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const IIN_RE = /^\d{12}$/;
export const BIN_RE = /^\d{12}$/;
export const NATIONAL_ID_RE = /^[A-Z0-9-]{4,50}$/;
export const IBAN_RE = /^[A-Z0-9]{20}$/;

export function normalizeDocumentNumber(input: string | undefined | null): string {
  return (input ?? "").trim().toUpperCase();
}

export function normalizeIban(input: string | undefined | null): string {
  return (input ?? "").replace(/[\s-]+/g, "").toUpperCase();
}

export function normalizePhone(input: string | undefined | null): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (trimmed.startsWith("+7")) {
    const subscriber = trimmed.slice(2).replace(/\D+/g, "").slice(0, 10);
    return subscriber ? `+7${subscriber}` : "";
  }
  const digits = trimmed.replace(/\D+/g, "");
  if (digits.length === 10) return `+7${digits}`;
  if (digits.length === 11 && (digits[0] === "7" || digits[0] === "8")) return `+7${digits.slice(1)}`;
  return trimmed;
}

export function isPhoneComplete(value: string | undefined | null): boolean {
  return PHONE_RE.test(normalizePhone(value));
}

export function phoneSubscriber(value: string | undefined | null): string {
  const normalized = normalizePhone(value);
  const body = normalized.startsWith("+7") ? normalized.slice(2) : normalized;
  return body.replace(/\D+/g, "").slice(0, 10);
}

export function formatPhoneDisplay(value: string | undefined | null): string {
  const digits = phoneSubscriber(value);
  if (!digits) return "";
  let out = `(${digits.slice(0, 3)}`;
  if (digits.length >= 3) out += ")";
  if (digits.length > 3) out += ` ${digits.slice(3, 6)}`;
  if (digits.length > 6) out += `-${digits.slice(6, 8)}`;
  if (digits.length > 8) out += `-${digits.slice(8, 10)}`;
  return out;
}

export interface PhoneEdit {
  value: string;
  caret: number;
}

function countDigitsBefore(str: string, index: number): number {
  let count = 0;
  const end = Math.min(index, str.length);
  for (let i = 0; i < end; i++) {
    if (str[i] >= "0" && str[i] <= "9") count++;
  }
  return count;
}

function caretIndexAfterDigits(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= "0" && formatted[i] <= "9") {
      count++;
      if (count === digitCount) return i + 1;
    }
  }
  return formatted.length;
}

export function applyPhoneEdit(
  rawValue: string,
  caret: number,
  prevValue: string | undefined | null,
): PhoneEdit {
  let digits = rawValue.replace(/\D+/g, "");
  if (digits.length === 11 && (digits[0] === "7" || digits[0] === "8")) {
    digits = digits.slice(1);
  }
  const prevDigits = phoneSubscriber(prevValue);
  let digitsBeforeCaret = countDigitsBefore(rawValue, caret);
  const removedSeparatorOnly =
    digits === prevDigits && rawValue.length < formatPhoneDisplay(prevValue).length && digitsBeforeCaret > 0;
  if (removedSeparatorOnly) {
    digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
    digitsBeforeCaret -= 1;
  }
  const subscriber = digits.slice(0, 10);
  const value = subscriber ? `+7${subscriber}` : "";
  const caretDigits = Math.min(digitsBeforeCaret, subscriber.length);
  return { value, caret: caretIndexAfterDigits(formatPhoneDisplay(value), caretDigits) };
}
